import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Settings2 } from "lucide-react";

type BillingMode = "per_show" | "multi_bill_split" | "multi_bill_combined";

interface MatrixOccurrence {
  occurrenceDate: string; // YYYY-MM-DD
  venueAddress?: string;
  eventTime?: string;
  rate?: string;
  talentIds: string[]; // empty = inherit series default
}

interface Talent {
  id: string;
  stageName?: string | null;
  name?: string | null;
}

interface ClientOption {
  id: string;
  accountName: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected booking/client; if omitted the dialog shows a picker. */
  defaultClientId?: string;
  /** Optional callback fired after the series is created. */
  onCreated?: (seriesId: string) => void;
}

/**
 * Advanced Booking matrix dialog (Phase 2).
 *
 * One popup that designs an entire recurring/multi-date series:
 *   • Rows = planned dates (manually added or generated from a cadence)
 *   • Columns = talents the rep wants on the bill
 *   • Cells = checkbox + per-date overrides (venue / time / rate)
 *   • Billing mode = per-show / split (N invoices per date) / combined
 *
 * Submits to POST /api/event-series which seeds the series row + one
 * event_series_occurrences row per planned date. The daily sweep then
 * mints invoices honouring each row's overrides + the chosen billingMode.
 */
export function AdvancedBookingMatrixDialog({ open, onOpenChange, defaultClientId, onCreated }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();

  const [clientId, setClientId] = useState<string>(defaultClientId ?? "");
  const [name, setName] = useState("");
  const [billingMode, setBillingMode] = useState<BillingMode>("per_show");
  const [rate, setRate] = useState<string>("");
  const [bookingDetails, setBookingDetails] = useState<string>("");
  const [autoSendInvoice, setAutoSendInvoice] = useState(true);
  const [stopOnUnpaid, setStopOnUnpaid] = useState(false);
  const [preflightDays, setPreflightDays] = useState<number>(3);
  const [seriesTalentIds, setSeriesTalentIds] = useState<string[]>([]);
  const [occurrences, setOccurrences] = useState<MatrixOccurrence[]>([]);
  const [newDate, setNewDate] = useState("");

  // Optional cadence helper — fills the date column from a recurring rule.
  const [cadence, setCadence] = useState<"weekly" | "biweekly" | "monthly" | "every_n_days">("weekly");
  const [cadenceN, setCadenceN] = useState<number>(1);
  const [cadenceCount, setCadenceCount] = useState<number>(4);
  const [cadenceStart, setCadenceStart] = useState<string>("");

  const { data: talents = [] } = useQuery<Talent[]>({ queryKey: ["/api/talent"] });
  const { data: clients = [] } = useQuery<ClientOption[]>({
    queryKey: ["/api/clients", "lookup"],
    enabled: !defaultClientId,
  });

  const talentLabel = (t: Talent) => t.stageName || t.name || t.id;

  const addManualDate = () => {
    if (!newDate) return;
    setOccurrences((prev) =>
      [...prev, { occurrenceDate: newDate, talentIds: [] }].sort((a, b) =>
        a.occurrenceDate.localeCompare(b.occurrenceDate),
      ),
    );
    setNewDate("");
  };

  const generateFromCadence = () => {
    if (!cadenceStart) {
      toast({ title: "Pick a start date", variant: "destructive" });
      return;
    }
    const dates: MatrixOccurrence[] = [];
    const start = new Date(cadenceStart);
    for (let i = 0; i < Math.min(cadenceCount, 52); i++) {
      const d = new Date(start.getTime());
      switch (cadence) {
        case "weekly":
          d.setDate(start.getDate() + i * 7);
          break;
        case "biweekly":
          d.setDate(start.getDate() + i * 14);
          break;
        case "monthly":
          d.setMonth(start.getMonth() + i);
          break;
        case "every_n_days":
          d.setDate(start.getDate() + i * Math.max(1, cadenceN));
          break;
      }
      dates.push({ occurrenceDate: d.toISOString().slice(0, 10), talentIds: [] });
    }
    setOccurrences(dates);
  };

  const removeOccurrence = (idx: number) => {
    setOccurrences((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateOccurrence = (idx: number, patch: Partial<MatrixOccurrence>) => {
    setOccurrences((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };

  const toggleSeriesTalent = (talentId: string) => {
    setSeriesTalentIds((prev) =>
      prev.includes(talentId) ? prev.filter((t) => t !== talentId) : [...prev, talentId],
    );
  };

  const toggleOccurrenceTalent = (idx: number, talentId: string) => {
    setOccurrences((prev) =>
      prev.map((o, i) => {
        if (i !== idx) return o;
        const has = o.talentIds.includes(talentId);
        return {
          ...o,
          talentIds: has ? o.talentIds.filter((t) => t !== talentId) : [...o.talentIds, talentId],
        };
      }),
    );
  };

  const effectiveTalentsForRow = (o: MatrixOccurrence): string[] =>
    o.talentIds.length > 0 ? o.talentIds : seriesTalentIds;

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        clientId,
        name: name.trim() || undefined,
        cadence,
        cadenceN,
        billingMode,
        talentIds: seriesTalentIds,
        rate: rate || null,
        bookingDetails: bookingDetails || null,
        autoSendInvoice,
        stopOnUnpaid,
        preflightDays,
        occurrences: occurrences.map((o) => ({
          occurrenceDate: new Date(o.occurrenceDate).toISOString(),
          venueAddress: o.venueAddress || null,
          eventTime: o.eventTime || null,
          rate: o.rate || null,
          talentIds: o.talentIds,
        })),
      };
      return apiRequest("POST", "/api/event-series", payload).then((r) => r.json());
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ["/api/event-series"] });
      qc.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Series created",
        description: `${resp.occurrenceCount} dates scheduled. Invoices will generate on the daily sweep.`,
      });
      onCreated?.(resp.series.id);
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({
        title: "Failed to create series",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const canSubmit = useMemo(() => {
    if (!clientId) return false;
    if (occurrences.length === 0) return false;
    if (billingMode === "multi_bill_split" && seriesTalentIds.length < 2) return false;
    return true;
  }, [clientId, occurrences.length, billingMode, seriesTalentIds.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dialog-advanced-booking">
        <DialogHeader>
          <DialogTitle>Advanced Booking — Multi-date / Multi-talent</DialogTitle>
          <DialogDescription>
            Plan a recurring event, a multi-date run, or a tour. Set up dates, talents, and per-date overrides
            in one popup. Each row becomes one occurrence; the daily sweep mints invoices on schedule.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Booking + series basics */}
          <div className="grid grid-cols-2 gap-3">
            {!defaultClientId && (
              <div>
                <Label htmlFor="matrix-client">Booking / client</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger id="matrix-client" data-testid="select-matrix-client">
                    <SelectValue placeholder="Pick a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.accountName || c.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="matrix-name">Series name (optional)</Label>
              <Input
                id="matrix-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Spring '26 East Coast Tour"
                data-testid="input-matrix-name"
              />
            </div>
            <div>
              <Label htmlFor="matrix-rate">Default rate per show ($)</Label>
              <Input
                id="matrix-rate"
                type="number"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.00"
                data-testid="input-matrix-rate"
              />
            </div>
            <div>
              <Label htmlFor="matrix-billing">Billing mode</Label>
              <Select value={billingMode} onValueChange={(v) => setBillingMode(v as BillingMode)}>
                <SelectTrigger id="matrix-billing" data-testid="select-matrix-billing">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_show">Per show — 1 invoice per date</SelectItem>
                  <SelectItem value="multi_bill_split">Split — 1 invoice per talent per date</SelectItem>
                  <SelectItem value="multi_bill_combined">Combined — 1 invoice covers all talent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Talents on this series */}
          <div>
            <Label>Talent on this series</Label>
            <div className="flex flex-wrap gap-2 pt-1">
              {talents.map((t) => (
                <label
                  key={t.id}
                  className="flex items-center gap-2 px-2 py-1 border rounded-md hover-elevate active-elevate-2 cursor-pointer text-sm"
                  data-testid={`chip-series-talent-${t.id}`}
                >
                  <Checkbox
                    checked={seriesTalentIds.includes(t.id)}
                    onCheckedChange={() => toggleSeriesTalent(t.id)}
                  />
                  {talentLabel(t)}
                </label>
              ))}
              {talents.length === 0 && (
                <p className="text-sm text-muted-foreground">No talent profiles yet. Add talent first.</p>
              )}
            </div>
          </div>

          {/* Cadence helper to bulk-fill dates */}
          <div className="border rounded-md p-3 bg-muted/30">
            <div className="text-sm font-medium mb-2">Generate dates from a cadence (optional)</div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <Label>Start date</Label>
                <Input
                  type="date"
                  value={cadenceStart}
                  onChange={(e) => setCadenceStart(e.target.value)}
                  data-testid="input-cadence-start"
                />
              </div>
              <div>
                <Label>Repeat</Label>
                <Select value={cadence} onValueChange={(v) => setCadence(v as any)}>
                  <SelectTrigger data-testid="select-cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Every 2 weeks</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="every_n_days">Every N days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cadence === "every_n_days" && (
                <div>
                  <Label>N (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={cadenceN}
                    onChange={(e) => setCadenceN(parseInt(e.target.value, 10) || 1)}
                  />
                </div>
              )}
              <div>
                <Label># of dates</Label>
                <Input
                  type="number"
                  min={1}
                  max={52}
                  value={cadenceCount}
                  onChange={(e) => setCadenceCount(parseInt(e.target.value, 10) || 1)}
                />
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={generateFromCadence}
              data-testid="button-generate-cadence-dates"
            >
              Fill dates from cadence
            </Button>
          </div>

          {/* Manual add row */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="matrix-add-date">Add a single date</Label>
              <Input
                id="matrix-add-date"
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                data-testid="input-add-date"
              />
            </div>
            <Button onClick={addManualDate} size="sm" data-testid="button-add-date">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          {/* The matrix */}
          {occurrences.length > 0 && (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-2 py-2">Date</th>
                    {seriesTalentIds.map((tid) => {
                      const t = talents.find((x) => x.id === tid);
                      return (
                        <th key={tid} className="px-2 py-2 text-center">
                          {t ? talentLabel(t) : tid}
                        </th>
                      );
                    })}
                    {seriesTalentIds.length === 0 && (
                      <th className="px-2 py-2 text-muted-foreground">(pick talent above to add columns)</th>
                    )}
                    <th className="px-2 py-2 text-right">Per-date overrides</th>
                  </tr>
                </thead>
                <tbody>
                  {occurrences.map((o, idx) => (
                    <tr key={`${o.occurrenceDate}-${idx}`} className="border-t" data-testid={`row-occurrence-${idx}`}>
                      <td className="px-2 py-2 whitespace-nowrap">{o.occurrenceDate}</td>
                      {seriesTalentIds.map((tid) => (
                        <td key={tid} className="px-2 py-2 text-center">
                          <Checkbox
                            checked={effectiveTalentsForRow(o).includes(tid)}
                            onCheckedChange={() => toggleOccurrenceTalent(idx, tid)}
                            data-testid={`checkbox-occurrence-${idx}-talent-${tid}`}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2 text-right">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`button-occurrence-overrides-${idx}`}>
                              <Settings2 className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 space-y-2" align="end">
                            <div>
                              <Label>Venue address</Label>
                              <Input
                                value={o.venueAddress ?? ""}
                                onChange={(e) => updateOccurrence(idx, { venueAddress: e.target.value })}
                                placeholder="(inherits from booking)"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label>Time</Label>
                                <Input
                                  value={o.eventTime ?? ""}
                                  onChange={(e) => updateOccurrence(idx, { eventTime: e.target.value })}
                                  placeholder="7:00 PM"
                                />
                              </div>
                              <div>
                                <Label>Rate ($)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={o.rate ?? ""}
                                  onChange={(e) => updateOccurrence(idx, { rate: e.target.value })}
                                  placeholder="(default)"
                                />
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeOccurrence(idx)}
                          data-testid={`button-remove-occurrence-${idx}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Series-level toggles */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={autoSendInvoice}
                onCheckedChange={(v) => setAutoSendInvoice(!!v)}
                data-testid="checkbox-auto-send"
              />
              Auto-send invoices to client when generated
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={stopOnUnpaid}
                onCheckedChange={(v) => setStopOnUnpaid(!!v)}
                data-testid="checkbox-stop-on-unpaid"
              />
              Pause cadence if a prior invoice is unpaid
            </label>
            <div>
              <Label htmlFor="preflight">Notify rep N days before each show</Label>
              <Input
                id="preflight"
                type="number"
                min={0}
                value={preflightDays}
                onChange={(e) => setPreflightDays(parseInt(e.target.value, 10) || 0)}
                data-testid="input-preflight-days"
              />
            </div>
            <div>
              <Label htmlFor="details">Booking details (snapshot on every invoice)</Label>
              <Textarea
                id="details"
                rows={2}
                value={bookingDetails}
                onChange={(e) => setBookingDetails(e.target.value)}
                placeholder="e.g. 60-min show, sound provided"
                data-testid="textarea-booking-details"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-matrix-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit || createMutation.isPending}
            data-testid="button-matrix-submit"
          >
            {createMutation.isPending ? "Creating…" : `Create ${occurrences.length} occurrences`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
